import { createFileRoute, Link } from "@tanstack/react-router";
import { IMAGES, BBN_PHOTOS } from "@/lib/images";
import { HeroBgImage } from "@/components/HeroBgImage";
import {
  ArrowRight,
  Phone,
  Award,
  Sun,
  Shield,
  Eye,
  Wrench,
  CheckCircle2,
  Building2,
  Sparkles,
  AlertTriangle,
} from "lucide-react";
import {
  GuideRelated,
  buildGuideSchemas,
  type GuideMeta,
  type FaqItem,
} from "@/components/GuideLayout";
import { Reveal } from "@/components/Reveal";
import { PostHeroConversion } from "@/components/sections/PostHeroConversion";

/**
 * Jämförelseartikel — "solfilm fönster bäst i test".
 *
 * Söktermen drar hög köpintention (171 klick, 8 konv. i Google Ads-data).
 * Tonen är ärlig och kriterie-driven snarare än "vi är bäst" — vi förklarar
 * VAD som avgör kvalitet och positionerar 3M + professionell installation
 * som det kvalitetsmedvetna valet, utan att nedvärdera namngivna konkurrenter.
 *
 * Värden för 3M Prestige är hämtade från 3M:s officiella produktblad
 * (ref. 1173225O, 1/4" Clear glas) — samma källa som /3m-prestige.
 */

const criteria = [
  {
    icon: Sun,
    title: "Värmereduktion (TSER)",
    short: "Andelen total solenergi som blockeras. Mätstandard: ASHRAE/NFRC.",
    detail:
      "Titta på TSER (Total Solar Energy Rejected) när du jämför hur mycket värme filmerna avvisar. Spektralfilm i de högre serierna når 40–60 % TSER och behåller klar sikt. Reflekterande filmer kan nå 65–80 %, men gör glaset mörkare. En siffra för enbart IR-reduktion säger lite om hur svalt rummet blir.",
  },
  {
    icon: Shield,
    title: "UV-skydd",
    short: "Andelen ultraviolett strålning som blockeras (300–380 nm).",
    detail:
      "En fönsterfilm av hög kvalitet bör blockera ≥ 99 % UV. För 3M Prestige anges 99 % UV-blockering. UV-strålningen är den enskilt största orsaken till att möbler, parkett och konst bleks. Ligger värdet under 99 % räknar vi inte filmen till de högre kvalitetsklasserna.",
  },
  {
    icon: Eye,
    title: "Optisk klarhet & färgneutralitet",
    short: "Hur mycket sikten förändras — färgton, slöja, reflexer inifrån.",
    detail:
      "Billig film får ofta en lila/brun ton efter några år (limmet och färgskiktet bryts ned av just det UV den ska blockera). Nanokeramisk film (t.ex. 3M Prestige) använder metallfria skikt som inte oxiderar — färgen ligger stabilt över 15+ år. Be alltid att få se en monterad referens på riktigt glas.",
  },
  {
    icon: Award,
    title: "Garantitid & garantitäckning",
    short: "Hur lång produktgaranti tillverkaren ger — och vad den täcker.",
    detail:
      "Tillverkare av film i de högre kvalitetsklasserna ger 10–15 års materialgaranti vid auktoriserad installation. Ofta ingår byte vid bubblor, missfärgning eller delaminering. Läs villkoren extra noga om garantin är kort (2–5 år) eller bara gäller när tillverkaren själv monterat filmen.",
  },
  {
    icon: Wrench,
    title: "Installation — professionell vs. egen",
    short: "Den enskilt största faktorn bakom dåligt resultat är fel montering.",
    detail:
      "Bubblor, kantsläpp, damm under filmen och felaktig avdragstid på 2-/3-glas är nästan alltid installationsproblem, inte produktproblem. Auktoriserad installation av certifierad tekniker krävs dessutom för att tillverkargarantin ska gälla. Egenmontage från byggvaruhandel sparar oftast bara några hundralappar — och förlorar garantin direkt.",
  },
  {
    icon: Sparkles,
    title: "Energi- & komfortverkan på plats",
    short: "Subjektiv komfort: bländning, hotspots, drag mot fönster.",
    detail:
      "Du behöver också prova hur filmen känns vid fönstret. Bra solfilm minskar hettan när solen ligger på rutan. Be om en provbit att hålla mot ditt eget glas i fullt solljus innan du bestämmer dig.",
  },
];

const comparisonRows = [
  {
    label: "Värmereduktion (TSER)",
    klar: "50 %",
    spegel: "60–80 %",
    bygg: "20–40 %*",
  },
  {
    label: "Synligt ljus (VLT)",
    klar: "68 %",
    spegel: "15–35 %",
    bygg: "varierar",
  },
  {
    label: "UV-blockering",
    klar: "≥ 99 %",
    spegel: "≥ 99 %",
    bygg: "ofta 95–99 %",
  },
  {
    label: "Förväntad livslängd",
    klar: "10–15 år",
    spegel: "10–15 år",
    bygg: "3–7 år*",
  },
  {
    label: "Produktgaranti",
    klar: "10 år",
    spegel: "10 år",
    bygg: "0–2 år",
  },
  {
    label: "Krav på fönstrets ursprungsgaranti",
    klar: "Påverkas ej",
    spegel: "Påverkas ej",
    bygg: "Risk vid fel film/glas",
  },
];

const apartmentChecklist = [
  "Bostadsrätt: kolla föreningens stadgar — ändring av fönstrets utseende utåt kräver oftast styrelsegodkännande. Klar film (t.ex. 3M Prestige) syns nästan inte utifrån och godkänns nästan alltid utan diskussion.",
  "Hyresrätt: fastighetsägaren måste godkänna innan montering. Vi tar fram en installationsbeskrivning du kan skicka in — det räcker oftast.",
  "Liten yta = lågt totalpris, men hög pris/m². Räkna med 1 800–2 800 kr per fönster i ett typiskt lägenhetsprojekt (3–6 fönster).",
  "Värmen i en lägenhet kommer ofta från EN sida — sydväst eller väst. Då räcker det att filma de fönstren, inte hela bostaden.",
  "Lägenhetsmontering går snabbt: 3–6 fönster monteras typiskt på under 3 timmar och utan flytt av möbler.",
];

export const faqs: FaqItem[] = [
  {
    q: "Finns det ett officiellt 'bäst i test' för solfilm i Sverige?",
    a: "Nej, varken Råd & Rön, Testfakta eller motsvarande oberoende institut har gjort ett aktuellt jämförande test av fastighets-solfilm i Sverige. Påståenden om 'bäst i test' i marknadsföring bygger nästan alltid på tillverkarens egna mätningar eller äldre utländska tester. Det enda objektiva sättet att jämföra är att titta på officiella datablad (TSER, VLT, UV-blockering, garantitid) och referensinstallationer.",
  },
  {
    q: "Vilken solfilm är bäst för fönster i en bostad?",
    a: "Det beror på vad du vill lösa. För maximal värmereduktion utan att förlora dagsljus är spektralt selektiv film som 3M Prestige 70 (50 % TSER, 68 % VLT) ofta förstavalet. För insynsskydd dagtid + värmereduktion väljs en spegel- eller silverfilm. För säkerhet kombineras solfilm med en tjock säkerhetsfilm (3M Ultra). En auktoriserad installatör räknar fram rätt val utifrån väderstreck, glastyp och målbild.",
  },
  {
    q: "Är dyrare solfilm verkligen bättre?",
    a: "Ja, men dubbelt pris betyder inte dubbelt så bra film. Skillnaden mellan billig byggvaruhandelsfilm och högre kvalitetsklasser (3M Prestige, Solar Screen) syns tydligt i livslängd (15+ år vs 3–7), färgneutralitet, UV-skydd och garanti. Skillnaden mellan två serier i de högre kvalitetsklasserna från olika tillverkare är däremot ofta liten. Då avgörs valet av vilken filmtyp som passar fönstret och vilken installatör som står bakom monteringen.",
  },
  {
    q: "Kan jag montera solfilm själv för att spara pengar?",
    a: "Du kan, men du sparar oftast bara 300–600 kr per fönster och förlorar i regel både tillverkargarantin och installationsgarantin. Vanliga fel vid egenmontage är damm under filmen, bubblor som inte försvinner, kantsläpp efter ett par månader och i värsta fall sprucken termoruta vid felaktigt filmval på isolerglas. På enstaka små rutor kan egenmontering fungera. För en hel bostad eller arbetsplats rekommenderar vi professionell installation.",
  },
  {
    q: "Hur fungerar solfilm i en lägenhet?",
    a: "I praktiken precis som i en villa, men med två tillägg: (1) fastighetsägaren eller bostadsrättsföreningen ska godkänna ändringen, och (2) ytan per fönster är ofta mindre vilket gör att priset per m² blir högre men totalpriset lågt. Klar film som 3M Prestige syns knappt utifrån och godkänns oftast utan invändningar. Vi hjälper till med underlaget till styrelsen om det behövs.",
  },
];

const meta: GuideMeta = {
  category: "Jämförelse · Solfilm",
  title: "Solfilm bäst i test — vad avgör egentligen kvaliteten?",
  tldr:
    "Det finns ingen officiell 'bäst i test'-rangordning av fastighets-solfilm i Sverige. Det som faktiskt avgör kvalitet är TSER, UV-blockering, optisk klarhet, garantitid och installationskvalitet. Premiumfilm från 3M (Prestige-serien) monterad av auktoriserad installatör är genomgående det säkraste valet — både för villa och lägenhet.",
  readTimeMin: 9,
  path: "/solfilm-bast-i-test",
  updated: "2026-06-16",
  faqs,
  related: [
    { to: "/3m-prestige", label: "3M Prestige — premium klar solfilm" },
    { to: "/solfilm-privat", label: "Solfilm för bostad — produktöversikt" },
    { to: "/solfilm-foretag", label: "Solfilm för företag" },
    { to: "/auktoriserad-installator", label: "Hitta auktoriserad installatör" },
    { to: "/priser", label: "Priser & kostnad per m²" },
    { to: "/referenser", label: "Genomförda projekt" },
    { to: "/datablad", label: "Tekniska datablad (3M, Solar Screen)" },
    { to: "/vanliga-fragor", label: "Vanliga frågor om solfilm" },
  ],
};

export const Route = createFileRoute("/solfilm-bast-i-test")({
  head: () => ({
    meta: [
      {
        title:
          "Solfilm bäst i test — så avgör du kvalitet (TSER, UV, garanti) | BBN Nordic",
      },
      {
        name: "description",
        content:
          "Finns ingen officiell 'bäst i test' av solfilm i Sverige. Vi förklarar vad som faktiskt avgör kvalitet — värmereduktion, UV-skydd, garanti, installation — och hur 3M Prestige står sig.",
      },
      { property: "og:title", content: "Solfilm bäst i test — jämförelseguide" },
      {
        property: "og:description",
        content:
          "TSER, UV-blockering, garanti och installation — det är vad som avgör. Ärlig jämförelse av klar, spegel och premium solfilm för villa och lägenhet.",
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://bbnnordic.se/solfilm-bast-i-test" },
      { property: "og:image", content: IMAGES.solfilmHero },
    ],
    links: [{ rel: "canonical", href: "https://bbnnordic.se/solfilm-bast-i-test" }],
    scripts: buildGuideSchemas(meta),
  }),
  component: Page,
});

function Page() {
  return (
    <main className="bg-background">
      {/* Hero */}
      <section className="relative pt-24 pb-10 md:pt-40 md:pb-16 bg-navy overflow-hidden">
        <HeroBgImage src={BBN_PHOTOS.prestigeHero} alt="3M Prestige-solfilm installerad — jämförelsetest" />
        <div className="relative mx-auto max-w-4xl px-5 md:px-10">
          <Reveal>
            <div className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-gold-bright font-semibold mb-4">
              <Award size={14} />
              <span>Jämförelse · Solfilm</span>
              <span className="text-cream/70">·</span>
              <span className="text-cream/80">{meta.readTimeMin} min läsning</span>
            </div>
            <h1 className="font-display text-cream text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              Solfilm <em className="italic text-gold">bäst i test</em> — så avgör du kvalitet
            </h1>
            <aside className="mt-8 rounded-2xl border border-gold/30 bg-gold/10 backdrop-blur-sm p-5 md:p-7">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-gold-bright font-semibold mb-3">
                <Sparkles size={12} /> Kort svar
              </div>
              <p className="text-cream/95 text-base md:text-lg leading-relaxed">
                Det finns <strong className="text-gold-bright">ingen officiell oberoende
                "bäst i test"-rangordning</strong> av fastighets-solfilm i Sverige. Det som faktiskt
                skiljer kvalitet är fem mätbara saker:{" "}
                <strong className="text-gold-bright">TSER (värmereduktion), UV-blockering,
                optisk klarhet, garantitid och installationskvalitet</strong>. På de punkterna
                står sig 3M:s Prestige-serie monterad av auktoriserad installatör konsekvent
                högt, både för villa och lägenhet.
              </p>
            </aside>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center">
              <Link
                to="/offert"
                className="btn-glow inline-flex items-center justify-center gap-2 bg-gold text-navy px-6 py-3 rounded-full font-semibold min-h-11"
              >
                Få kostnadsfri jämförelse <ArrowRight size={16} />
              </Link>
              <Link
                to="/referenser"
                className="inline-flex items-center justify-center gap-2 text-cream/85 px-4 py-3 font-medium hover:text-gold-bright transition-colors min-h-11"
              >
                Se genomförda projekt
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <PostHeroConversion />

      {/* Sektion: ärlig disclaimer + sökintention */}
      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-3xl px-5 md:px-10">
          <Reveal>
            <p className="section-number">Det du behöver veta först</p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl text-foreground">
              "Bäst i test" finns inte — så här jämför du istället
            </h2>
            <p className="mt-5 text-foreground/85 leading-relaxed">
              När du söker på "solfilm bäst i test" hittar du framför allt återförsäljares
              egna sidor som påstår sig vara bäst. Det är inte ett oberoende test — det är
              marknadsföring. Råd & Rön, Testfakta eller motsvarande institut har inte gjort
              något aktuellt jämförande test av fastighetsfilm i Sverige.
            </p>
            <p className="mt-4 text-foreground/85 leading-relaxed">
              Det betyder inte att du står utan vägledning. Tillverkarna mäter sina produkter
              enligt internationella standarder (ASHRAE/NFRC), publicerar fullständiga
              datablad och låter ackrediterade laboratorier verifiera värdena. Med rätt
              kriterier kan du jämföra precis lika rigoröst som ett konsumenttest skulle göra.
            </p>
            <aside className="mt-6 rounded-xl border border-gold/30 bg-gold/5 p-5 flex items-start gap-3">
              <AlertTriangle size={20} className="text-gold shrink-0 mt-0.5" />
              <p className="text-sm text-foreground/85 leading-relaxed">
                <strong className="text-foreground">Varningsflagg vid jämförelse:</strong>{" "}
                produkter som bara anger "IR-reduktion" utan TSER, garantier under 5 år,
                UV-blockering under 99 %, eller leverantörer som inte vill visa officiellt
                datablad. Be om PDF-databladet. Seriösa tillverkare lämnar ut det.
              </p>
            </aside>
          </Reveal>
        </div>
      </section>

      {/* Kriterier */}
      <section className="py-12 md:py-16 bg-card/30 border-y border-border">
        <div className="mx-auto max-w-6xl px-5 md:px-10">
          <Reveal>
            <p className="section-number">De sex kriterierna</p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl text-foreground">
              Vad avgör om solfilm är "bra"?
            </h2>
            <p className="mt-4 max-w-2xl text-foreground/75 leading-relaxed">
              Gå igenom de här sex punkterna för varje solfilm du funderar på.
              Då ser du hur filmerna skiljer sig, oavsett vilket namn som står på förpackningen.
            </p>
          </Reveal>
          <div className="mt-10 grid md:grid-cols-2 gap-5">
            {criteria.map((c, i) => {
              const Icon = c.icon;
              return (
                <Reveal key={c.title} delay={i * 0.05}>
                  <article className="h-full rounded-2xl border border-gold/20 bg-card/70 p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="rounded-lg bg-gold/15 p-2">
                        <Icon size={20} className="text-gold" />
                      </div>
                      <h3 className="font-display text-lg md:text-xl text-foreground">
                        {i + 1}. {c.title}
                      </h3>
                    </div>
                    <p className="text-sm font-semibold text-gold-bright mb-2">{c.short}</p>
                    <p className="text-foreground/80 leading-relaxed text-sm">{c.detail}</p>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Jämförelsetabell */}
      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-5xl px-5 md:px-10">
          <Reveal>
            <p className="section-number">Jämförelse i siffror</p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl text-foreground">
              Klar premium vs spegel vs byggvaruhandelsfilm
            </h2>
            <p className="mt-4 max-w-2xl text-foreground/75 leading-relaxed">
              Spannet nedan speglar 3M:s officiella produktdata för Prestige- och Silver-serien
              samt en typisk billig polyester-solfilm från handelsledet. Exakta värden finns på
              respektive datablad — vi länkar till PDF:erna under <Link to="/datablad" className="text-gold-bright underline-offset-2 hover:underline">/datablad</Link>.
            </p>
          </Reveal>
          <Reveal>
            <div className="mt-8 overflow-x-auto rounded-2xl border border-border bg-card/50">
              <table className="w-full text-sm">
                <thead className="bg-navy/95 text-cream">
                  <tr>
                    <th className="text-left p-4 font-semibold">Egenskap</th>
                    <th className="text-left p-4 font-semibold">Klar premium<br /><span className="text-xs font-normal text-cream/70">(3M Prestige 70)</span></th>
                    <th className="text-left p-4 font-semibold">Spegel/Silver<br /><span className="text-xs font-normal text-cream/70">(3M Silver 20)</span></th>
                    <th className="text-left p-4 font-semibold">Byggvaruhandelsfilm<br /><span className="text-xs font-normal text-cream/70">(generisk)</span></th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((row, idx) => (
                    <tr
                      key={row.label}
                      className={idx % 2 === 0 ? "bg-card/30" : "bg-card/10"}
                    >
                      <td className="p-4 font-medium text-foreground">{row.label}</td>
                      <td className="p-4 text-foreground/80">{row.klar}</td>
                      <td className="p-4 text-foreground/80">{row.spegel}</td>
                      <td className="p-4 text-foreground/70">{row.bygg}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              * Varierar kraftigt mellan tillverkare. Många billiga filmer i handelsledet
              kommer från olika OEM-fabriker utan långtidsdata för svenskt klimat.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Vårt val + varför 3M */}
      <section className="py-16 md:py-20 bg-navy/95 border-y border-gold/20">
        <div className="mx-auto max-w-4xl px-5 md:px-10">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-gold-bright font-semibold">
              Varför vi väljer 3M Prestige
            </p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl text-cream">
              Därför rekommenderar vi 3M Prestige + auktoriserad installation
            </h2>
            <p className="mt-5 text-cream/85 leading-relaxed">
              Vi har testat och monterat de flesta större varumärken sedan 2008.
              Det finns flera bra tillverkare, men i vårt arbete har 3M Prestige-serien
              gett bäst resultat på de sex kriterierna ovan. 3M lämnar <strong className="text-gold-bright">10 års
              produktgaranti</strong> vid auktoriserad installation.
            </p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-3">
              {[
                "Spektralt selektiv nanoteknik — hög värmereduktion utan att mörka rummet",
                "99 % UV-blockering enligt 3M:s officiella datablad",
                "Metallfri konstruktion — påverkar inte mobil/wifi och oxiderar inte med tiden",
                "15–20 års förväntad livslängd — knappt något åldrande",
                "10 års produktgaranti via 3M + 10 års installationsgaranti från oss",
                "Auktoriserad installation krävs för att garantin ska gälla — det är där vi kommer in",
              ].map((p) => (
                <li key={p} className="flex items-start gap-2 text-cream/90 text-sm">
                  <CheckCircle2 size={18} className="text-gold shrink-0 mt-0.5" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/3m-prestige"
                className="inline-flex items-center justify-center gap-2 bg-gold text-navy px-6 py-3 rounded-full font-semibold min-h-11"
              >
                Läs mer om 3M Prestige <ArrowRight size={16} />
              </Link>
              <Link
                to="/auktoriserad-installator"
                className="inline-flex items-center justify-center gap-2 border border-gold/40 text-cream px-6 py-3 rounded-full font-semibold hover:bg-gold/10 transition min-h-11"
              >
                Vad innebär auktoriserad?
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Lägenhet — separat sektion */}
      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-4xl px-5 md:px-10">
          <Reveal>
            <div className="flex items-center gap-3 mb-4">
              <div className="rounded-xl bg-gold/15 p-2.5">
                <Building2 size={22} className="text-gold" />
              </div>
              <p className="section-number m-0">Solfilm för lägenhet</p>
            </div>
            <h2 className="font-display text-3xl md:text-4xl text-foreground">
              Bor du i lägenhet? Här är vad som skiljer
            </h2>
            <p className="mt-5 text-foreground/85 leading-relaxed">
              Solfilm fungerar på samma sätt i lägenhet som i villa. Men innan du beställer
              finns några praktiska skillnader att ta hänsyn till.
            </p>
            <ul className="mt-6 space-y-3">
              {apartmentChecklist.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 rounded-xl border border-border bg-card/40 p-4"
                >
                  <CheckCircle2 size={18} className="text-gold shrink-0 mt-1" />
                  <span className="text-foreground/85 text-sm leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm text-muted-foreground">
              Behöver du underlag att skicka till föreningens styrelse eller hyresvärden? Vi
              skickar gratis en kort installationsbeskrivning med 3M:s datablad — räcker
              oftast för att få godkänt direkt.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy py-16 md:py-20 border-y border-gold/20">
        <div className="mx-auto max-w-3xl px-5 md:px-10 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-gold-bright font-semibold mb-5">
            <Shield size={14} /> Auktoriserad 3M-installatör · 10 års produktgaranti
          </div>
          <h2 className="font-display text-3xl md:text-5xl text-cream mb-5">
            Vill du jämföra på dina egna fönster?
          </h2>
          <p className="text-cream/80 max-w-xl mx-auto mb-8 leading-relaxed">
            Vi kommer ut, mäter upp och visar dig en monterad referensbit i fullt solljus — så
            att du ser skillnaden själv innan du bestämmer dig. Helt kostnadsfritt.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/offert"
              className="btn-glow inline-flex items-center justify-center gap-2 bg-gold text-navy px-7 py-4 rounded-full font-semibold min-h-12"
            >
              Be om kostnadsfri bedömning <ArrowRight size={18} />
            </Link>
            <Link
              to="/referenser"
              className="inline-flex items-center justify-center gap-2 border border-gold/40 text-cream px-6 py-4 rounded-full font-semibold hover:bg-gold/10 transition min-h-12"
            >
              Se genomförda projekt
            </Link>
            <a
              href="tel:+46771502502"
              className="inline-flex items-center justify-center gap-2 text-cream/85 px-4 py-4 font-medium hover:text-gold-bright transition min-h-12"
            >
              <Phone size={16} /> 0771-502 502
            </a>
          </div>
        </div>
      </section>

      <GuideRelated items={meta.related ?? []} />
    </main>
  );
}
